// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/system";
import { Close } from "@mui/icons-material";
import { useAppDispatch } from "Hooks";
import { DeleteProjectThunk } from "Features/project/projectThunk";
import { useState } from "react";
import stylesDef from "./styles";
import { getAllFoldersThunk } from "Features/folderList/folderListThunk";
import { datadogRum } from "@datadog/browser-rum";

interface DeleteProjectDialogProps {
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
  projectId: string;
}

const DeleteProjectDialog = ({ showDialog, setShowDialog, projectId }: DeleteProjectDialogProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const styles = stylesDef(theme);
  const [loading, setLoading] = useState(false);
  
  const onCancel = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setShowDialog(false);
  };

  const onDelete = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setLoading(true);
    try {
      await dispatch(DeleteProjectThunk(projectId));
      // Refresh the folders so their project counts reflect the deletion. getAllFoldersThunk
      // stores the result itself, so there is nothing to dispatch with what it returns - but it
      // throws on a failed response, and a spinner that never clears is the symptom this dialog
      // was fixed for. The delete itself has already happened by then, so the count being stale
      // is not worth stranding the dialog over.
      await dispatch(getAllFoldersThunk());
    } catch (error) {
      datadogRum.addError(error);
    } finally {
      setLoading(false);
      setShowDialog(false);
    }
  };

  return (
    <Dialog
      open={showDialog}
      onClose={() => setShowDialog(false)}
      keepMounted={false}
      sx={styles.dialog}
    >
      <Box sx={styles.titleSection}>
        <DialogTitle sx={styles.dialogTitle}>
          Delete Project
        </DialogTitle>
        <IconButton
          color="inherit"
          onClick={() => setShowDialog(false)}
          aria-label="Close"
          sx={styles.closeButton}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={styles.dialogContent}>
        <Typography variant="body1" sx={styles.dialogText}>
          You're deleting this project. This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button onClick={onCancel} sx={styles.cancelButton}>
          Close
        </Button>
        <Button
          onClick={onDelete}
          disabled={loading}
          variant="contained"
        >
          {loading ? (
            <CircularProgress size={24} sx={styles.buttonProgress} />
          ) : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DeleteProjectDialog;
